import { useEffect, useState } from "react";

import useAxios from "@hooks/use-axios";
import { BRAND_KYC_ENDPOINTS, KYC_VERIFIED_STATUS, STATUS_LOCKED_REASON } from "../misc/constants";

const useBrandKyc = (brandId) => {

    // show Alert is false as we are handling the error in the catch block and showing a toast message instead of an alert.
    const { axiosInstance, loading } = useAxios({ base: "ops", initialLoader: true, showAlert: false });
    // The status write runs on its own instance so a PATCH does not flip the
    // page-level `loading` the read owns — that swapped the whole tab for a
    // spinner on save.
    const { axiosInstance: statusInstance, loading: saving } = useAxios({ base: "ops", alertTypeOnSuccess: "toast" });
    // Its own instance as well, so attaching documents does not put the status
    // card's Apply button into a loading state it has nothing to do with.
    const { axiosInstance: docsInstance } = useAxios({ base: "ops", alertTypeOnSuccess: "toast" });
    // useAxios asks for confirmation before every DELETE, and `loading` only
    // turns on once it has been given — so a cancelled delete never spins.
    const { axiosInstance: deleteInstance, loading: deleting } = useAxios({ base: "ops", alertTypeOnSuccess: "toast" });
    // The document a delete is for, so only that row spins.
    const [deletingId, setDeletingId] = useState(null);
    const [kyc, setKyc] = useState(null);
    // What the select currently shows, which is not yet what is on record: the
    // status only moves when Apply is pressed.
    const [statusDraft, setStatusDraft] = useState("");

    const fetchKyc = async () => {
        const res = await axiosInstance.get(BRAND_KYC_ENDPOINTS.detail(brandId));
        const result = res.data || res;
        setKyc(result.data || result);
    };

    useEffect(() => {
        fetchKyc();
    }, [brandId]); // eslint-disable-line react-hooks/exhaustive-deps

    const documents = kyc?.kyc_docs || [];
    const timeline = kyc?.timeline || [];
    const status = kyc?.kyc_status || "";

    // Follows the record, so a fetch or a save resets whatever was drafted.
    useEffect(() => {
        setStatusDraft(status);
    }, [status]);

    // Nothing submitted means there is no KYC to judge, so the status is not
    // ops' to move yet. Verified is the other end: once a brand clears KYC the
    // status is terminal and ops cannot walk it back from here. Either way the
    // select is disabled, and the guard in applyStatus keeps a stale render
    // from slipping a PATCH through behind it.
    const isVerified = status === KYC_VERIFIED_STATUS;
    const canUpdateStatus = documents.length > 0 && !isVerified;
    const isStatusDirty = Boolean(statusDraft) && statusDraft !== status;

    const applyStatus = async () => {
        if (!canUpdateStatus || !isStatusDirty) return;
        await statusInstance.patch(BRAND_KYC_ENDPOINTS.status(brandId), { kyc_status: statusDraft });
        await fetchKyc();
    };

    // The second half of an upload: the files are in storage, this is what puts
    // them on the brand. Thrown back to the uploader so a failed attach leaves
    // the rows in place instead of clearing them.
    const attachDocuments = async (uploaded) => {
        await docsInstance.patch(BRAND_KYC_ENDPOINTS.docs(brandId), {
            kyc_docs: uploaded.map((doc) => ({ name: doc.label || doc.originalName || doc.name, url: doc.url }))
        });
        await fetchKyc();
    };

    const deleteDocument = async (doc) => {
        if (!doc?._id) return;

        setDeletingId(doc._id);
        try {
            await deleteInstance.delete(BRAND_KYC_ENDPOINTS.doc(brandId, doc._id));
        } finally {
            // A 404 means it was already deleted — re-reading brings the list
            // in line either way.
            await fetchKyc();
        }
    };

    return {
        // A refetch after a save must not blank the tab, so the spinner is only
        // for the first load.
        loading   : loading && !kyc,
        saving,
        documents,
        timeline,
        status,
        statusDraft,
        setStatusDraft,
        canUpdateStatus,
        isStatusDirty,
        statusNote: canUpdateStatus ? "" : (isVerified ? STATUS_LOCKED_REASON.VERIFIED : STATUS_LOCKED_REASON.NO_DOCS),
        applyStatus,
        attachDocuments,
        deleteDocument,
        isDeleting: (doc) => deleting && deletingId === doc._id
    };
};

export default useBrandKyc;
